import { Button } from "@tql/ui-components-react/tql/buttons";
import { useCallback, useContext, useRef, useState } from "react";

import { PrepayTenderModals } from "~/features/payment/components/prepay-tender-modals.tsx";
import { usePrepayTenderFlow } from "~/features/payment/hooks/use-prepay-tender-flow.ts";
import {
	QUOTE_REFRESH_REQUIRED_STATUS,
	useRefreshConflictedQuote,
	useSubmitTenderQuote,
	useSubmitUpdatedQuote,
} from "~/features/quote/hooks/use-shipment-summary-hooks.tsx";
import { getLtlFallbackMessage } from "~/features/quote/utils/ltl-error-handler.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { QuoteStatus } from "~/shared/constants/quote-status.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import { ddTenderQuote, toDdWorkflow } from "~/shared/utils/datadog-actions.ts";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";
import { sendTenderQuoteEvent } from "~/shared/utils/google-analytics-helper.ts";

import { QuoteDetailsContext } from "../../../../context/quote-details-context.tsx";
import styles from "./terms-of-service.module.css";

type TermsOfServiceProps = {
	readonly handleShowConfirmationModal: (show: boolean) => void;
	readonly handleShowTenderModal: (show: boolean) => void;
	readonly handleShowErrorModal: (show: boolean, message: string) => void;
	readonly shouldUpdateBeforeTender?: boolean;
	readonly isTenderDisabled?: boolean;
	// Tender-only gate. Save must never run this: users are allowed to save a
	// partially-complete quote and come back later (#550629). Removed once in
	// PR 230560 and accidentally reinstated by PR 243918 — do not re-add to
	// `handleSaveClick`.
	readonly onBeforeSubmit?: () => boolean;
	readonly onSaveSuccess?: () => void;
	readonly submitSave?: () => Promise<{
		status: number;
		data?: QuoteResponse;
		message?: string;
	}>;
};

const REFRESH_QUOTE_MESSAGE =
	"The quote has recently been updated by another user. Please select Refresh below to get the latest information.";

export const TermsOfService = ({
	handleShowConfirmationModal,
	handleShowTenderModal,
	handleShowErrorModal,
	shouldUpdateBeforeTender = true,
	isTenderDisabled,
	onBeforeSubmit,
	onSaveSuccess,
	submitSave,
}: TermsOfServiceProps) => {
	const baseUrl = getBaseUrl();
	const { quoteState, workflow } = useContext(QuoteDetailsContext);
	const { submitUpdatedQuote, isLoading: isUpdating } = useSubmitUpdatedQuote();
	const { submitTenderQuote, isLoading: isTendering } = useSubmitTenderQuote();
	const { refreshQuote, isRefreshing } = useRefreshConflictedQuote();
	const { isInternal } = useDeploymentType();
	const fallbackMessage = getLtlFallbackMessage(isInternal);

	const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);

	const finalizeTender = useCallback(
		(quote: QuoteResponse) => {
			sendTenderQuoteEvent(String(quote.id), workflow);
			const bookedCarrier = quoteState.carrierPrices.find(
				(c) => c.id === quoteState.selectedCarrierPriceId,
			);
			const rateAdj =
				bookedCarrier?.originalBrokerMargin != null &&
				bookedCarrier.originalBrokerMargin !== bookedCarrier.brokerMargin &&
				bookedCarrier.carrierRate > 0
					? `${(((bookedCarrier.brokerMargin - bookedCarrier.originalBrokerMargin) / bookedCarrier.carrierRate) * 100).toFixed(1)}%`
					: "-";
			ddTenderQuote(String(quote.id), rateAdj, toDdWorkflow(workflow));
			handleShowTenderModal(true);
		},
		[
			workflow,
			quoteState.carrierPrices,
			quoteState.selectedCarrierPriceId,
			handleShowTenderModal,
		],
	);

	const prepayFlow = usePrepayTenderFlow({
		submitTenderQuote,
		onPaymentError: (message) => handleShowErrorModal(true, message),
		onTenderError: (message) => handleShowErrorModal(true, message),
		onTenderSuccess: finalizeTender,
	});

	const isBusy =
		isUpdating || isTendering || prepayFlow.isProcessing || isRefreshing;

	useAutoSpinner(isBusy);

	// Tracks which action triggered the refresh modal so we can resume it
	// after the user confirms the refresh.
	const pendingActionRef = useRef<"save" | "tender" | null>(null);

	// Blocks duplicate submits fired before React commits the pending state,
	// e.g. a held Enter key repeating on the focused button.
	const inFlightRef = useRef(false);

	const handleRefreshConfirmed = useCallback(async () => {
		const action = pendingActionRef.current;
		pendingActionRef.current = null;
		setIsRefreshModalOpen(false);
		const result = await refreshQuote(quoteState.id);
		if (!result.success) {
			handleShowErrorModal(true, result.error || fallbackMessage);
			return;
		}
		// Surface a non-blocking notice that the page now reflects the latest
		// quote and the user can re-attempt their action.
		handleShowErrorModal(
			true,
			`The quote has been refreshed with the latest information. Please review and ${action ?? "try"} again.`,
		);
	}, [refreshQuote, quoteState.id, handleShowErrorModal, fallbackMessage]);

	const handleRefreshCancelled = useCallback(() => {
		pendingActionRef.current = null;
		setIsRefreshModalOpen(false);
	}, []);

	const handleSaveClick = async () => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		try {
			const submitResult = await (submitSave ?? submitUpdatedQuote)();
			if (submitResult.status === 200) {
				if (onSaveSuccess) {
					onSaveSuccess();
					return;
				}
				handleShowConfirmationModal(true);
				return;
			}
			if (submitResult.status === QUOTE_REFRESH_REQUIRED_STATUS) {
				pendingActionRef.current = "save";
				setIsRefreshModalOpen(true);
				return;
			}
			handleShowErrorModal(true, submitResult.message ?? fallbackMessage);
		} finally {
			inFlightRef.current = false;
		}
	};

	const runTender = async () => {
		let quoteForTender: QuoteResponse | Pick<QuoteResponse, "id" | "eTag">;

		if (shouldUpdateBeforeTender) {
			const submitResult = await submitUpdatedQuote();
			if (submitResult.status === QUOTE_REFRESH_REQUIRED_STATUS) {
				pendingActionRef.current = "tender";
				setIsRefreshModalOpen(true);
				return;
			}
			if (submitResult.status !== 200 || !submitResult.data) {
				handleShowErrorModal(true, submitResult.message ?? fallbackMessage);
				return;
			}
			quoteForTender = submitResult.data;
		} else {
			quoteForTender = {
				id: quoteState.id,
				eTag: quoteState.eTag,
			};
		}

		// Phase 1: initial tender call. Backend may respond with PaymentRequired
		// for external pre-paid customers; internal users go straight to success.
		const tenderResult = await submitTenderQuote(quoteForTender);
		if (tenderResult.status === QUOTE_REFRESH_REQUIRED_STATUS) {
			pendingActionRef.current = "tender";
			setIsRefreshModalOpen(true);
			return;
		}
		if (tenderResult.status !== 200 || !tenderResult.data) {
			handleShowErrorModal(true, tenderResult.message ?? fallbackMessage);
			return;
		}

		if (
			!isInternal &&
			tenderResult.data.statusLevel === QuoteStatus.PaymentRequired
		) {
			prepayFlow.startPrepayFlow(tenderResult.data);
			return;
		}

		finalizeTender(tenderResult.data);
	};

	const handleTenderClick = async () => {
		if (inFlightRef.current) return;
		if (onBeforeSubmit && !onBeforeSubmit()) return;
		inFlightRef.current = true;
		try {
			await runTender();
		} finally {
			inFlightRef.current = false;
		}
	};

	return (
		<div className={styles.termsOfServiceWrapper}>
			<PrepayTenderModals flow={prepayFlow} />
			{isRefreshModalOpen ? (
				<TraxModal
					cancelText={"Cancel"}
					confirmText={"Refresh"}
					onCancel={handleRefreshCancelled}
					onConfirm={() => void handleRefreshConfirmed()}
					showButtonsDisabled={isRefreshing}
					title={"Refresh Quote"}
					type={"confirmation"}
				>
					<p>{REFRESH_QUOTE_MESSAGE}</p>
				</TraxModal>
			) : null}
			<div>
				<div className={styles.termsOfServiceHeader}>Terms of Service</div>
				<p>
					By tendering a shipment to TQL, you acknowledge and understand
					TQL&apos;s LTL terms and conditions and knowingly, voluntarily and
					willfully agree to the terms and conditions.
				</p>
				<p>
					<a
						className={"tql-link"}
						href={`${baseUrl}/app/ltl/data/General_TermsConditions.pdf`}
						rel={"noopener noreferrer"}
						tabIndex={-1}
						target={"_blank"}
					>
						Terms and Conditions
					</a>
				</p>
			</div>
			<div className={styles.shipmentDetailsButtons}>
				<div>
					<Button
						disabled={isTenderDisabled}
						loading={isBusy}
						onClick={() => void handleTenderClick()}
						onKeyDown={(e: React.KeyboardEvent) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								if (e.repeat || isBusy) return;
								void handleTenderClick();
							}
						}}
						variant={"primary"}
					>
						Tender Shipment
					</Button>
				</div>
				<div>
					<Button
						loading={isBusy}
						onClick={() => void handleSaveClick()}
						tabIndex={-1}
					>
						Save
					</Button>
				</div>
			</div>
		</div>
	);
};
